import React from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Box, Grid, Typography } from "@mui/material";
import Footer from "../Components/Footer/Footer";
import useMetaTracking from "../hooks/useMetaTracking";

// Hero image
import HeroImg from "../Assets/vegan_soft_mini_buns.png";

// Product images
import VeganSoftCoconutBunImg from "../Assets/vegan_soft_coconut_bun.png";
import VeganSoftDurianBunImg from "../Assets/vegan_soft_durian_bun.png";
import VeganSoftTaroBunImg from "../Assets/vegan_soft_taro_bun.png";
import VeganSoftMungBeanBunImg from "../Assets/vegan_soft_mung bean_bun.png";

function VeganSMB() {
  useMetaTracking("ViewContent", {
    content_name: "Vegan Soft Mini Buns",
    content_category: "Vegan Menu",
    content_ids: ["vegan_soft_mini_buns"],
    content_type: "vegan_vietnamese_pastries",
    value: 4.00,
    currency: "USD",
  });

  const BASE_URL = "https://www.donghungvienbakery.com";
  const PAGE_PATH = "/vegan-soft-mini-buns";

  const veganBunItems = [
    { img: VeganSoftCoconutBunImg, title: "Vegan Soft Coconut Bun", url: "/vegan-soft-coconut-bun" },
    { img: VeganSoftDurianBunImg, title: "Vegan Soft Durian Bun", url: "/vegan-soft-durian-bun" },
    { img: VeganSoftTaroBunImg, title: "Vegan Soft Taro Bun", url: "/vegan-soft-taro-bun" },
    { img: VeganSoftMungBeanBunImg, title: "Vegan Soft Mung Bean Bun", url: "/vegan-soft-mung-bean-bun" },
  ];

  return (
    <HelmetProvider>
      <Helmet>
        <title>Vegan Soft Mini Buns | Đông Hưng Viên Bakery</title>
        <link rel="canonical" href={`${BASE_URL}${PAGE_PATH}`} />

        {/* Thumbnail / social preview */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${BASE_URL}${PAGE_PATH}`} />
        <meta property="og:title" content="Vegan Soft Mini Buns | Đông Hưng Viên Bakery" />
        <meta
          property="og:description"
          content="Made with layers of flaky dough twisted into a spiral shape, these visually striking pastries are soft and slightly chewy, perfectly complementing their smooth, flavorful fillings."
        />
        <meta property="og:image" content={`${BASE_URL}${HeroImg}`} />
        <meta property="og:image:alt" content="Vegan Soft Mini Buns at Đông Hưng Viên Bakery" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Vegan Soft Mini Buns | Đông Hưng Viên Bakery" />
        <meta
          name="twitter:description"
          content="Made with layers of flaky dough twisted into a spiral shape, these visually striking pastries are soft and slightly chewy, perfectly complementing their smooth, flavorful fillings."
        />
        <meta name="twitter:image" content={`${BASE_URL}${HeroImg}`} />
      </Helmet>

      <Box
        sx={{
          pt: 10,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          background: "#0f0f0f",
        }}
      >
        {/* Header / Intro Section */}
        <Box sx={{ py: 4 }}>
          <Box
            sx={{
              maxWidth: 1240,
              width: "82%",
              mx: "auto",
              px: { xs: 2, md: 0 },
            }}
          >
            <Grid container spacing={4} alignItems="center">
              {/* Image */}
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "center", md: "flex-end" },
                  }}
                >
                  <Box
                    component="img"
                    src={HeroImg}
                    alt="Vegan Soft Mini Buns"
                    sx={{
                      width: { xs: "90%", md: "100%" },
                      maxHeight: "400px",
                      objectFit: "cover",
                      borderRadius: "16px",
                    }}
                  />
                </Box>
              </Grid>

              {/* Text */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: "bold",
                      mb: 1,
                      fontSize: { xs: "24px", sm: "30px", md: "35px" },
                      textTransform: "uppercase",
                      color: "#fff",
                    }}
                  >
                    Vegan Soft Mini Buns
                  </Typography>

                  <Box sx={{ borderBottom: "1px solid #fff", mt: 2 }} />

                  {/* Description */}
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 4,
                      mb: 2,
                      lineHeight: 1.6,
                      fontSize: { xs: "15px", md: "16px" },
                      color: "#fff",
                    }}
                  >
                    Made with layers of flaky dough twisted into a spiral shape, these visually striking pastries are soft and slightly chewy, perfectly complementing their smooth, flavorful fillings.
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: 2,
                      lineHeight: 1.6,
                      fontSize: { xs: "15px", md: "16px" },
                      color: "#fff",
                    }}
                  >
                    Fillings include: coconut, taro, mung bean, and durian.
                  </Typography>

                  {/* Price */}
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 1.5,
                      lineHeight: 1.6,
                      fontSize: { xs: "18px", md: "20px" },
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  >
                    Price: $4.00 each
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#fff",
                      fontStyle: "italic",
                      fontSize: { xs: "14px", md: "15px" },
                    }}
                  >
                    Vegan
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Divider */}
        <Box
          sx={{
            borderBottom: "1px solid #fff",
            maxWidth: 1240,
            width: "82%",
            mx: "auto",
          }}
        />

        {/* Vegan Buns Grid */}
        <Box sx={{ py: 6 }}>
          <Box
            sx={{
              maxWidth: 1240,
              width: "82%",
              mx: "auto",
              px: { xs: 2, md: 0 },
            }}
          >
            <Grid container spacing={2} justifyContent="center">
              {veganBunItems.map((bun, i) => (
                <Grid item xs={10} sm={6} md={3} key={i}>
                  <Box
                    sx={{
                      textAlign: "center",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <a
                      href={`${BASE_URL}${bun.url}`}
                      style={{ textDecoration: "none", width: "100%" }}
                      rel="noopener noreferrer"
                    >
                      <Box
                        component="img"
                        src={bun.img}
                        alt={bun.title}
                        sx={{
                          width: "100%",
                          maxWidth: 320,
                          mx: "auto",
                          borderRadius: "30px",
                          aspectRatio: "4 / 3",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mt: 1.5,
                          color: "#fff",
                          letterSpacing: 0.4,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          fontSize: { xs: "12px", md: "13px" },
                          display: "block",
                        }}
                      >
                        {bun.title}
                      </Typography>
                    </a>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        <Box sx={{ mt: "auto" }}>
          <Footer />
        </Box>
      </Box>
    </HelmetProvider>
  );
}

export default VeganSMB;